import { z } from "zod/v4";

/**
 * One visitor's state on a visit (spec 2026-09-23 §2.1). The invite is only a
 * container (`active | revoked`); everything a person can be — waiting to
 * fill in, waiting for approval, usable, refused, revoked — lives here.
 */
export const guestStatuses = [
  "awaiting_visitor",
  "pending",
  "active",
  "rejected",
  "revoked",
] as const;
export const guestStatusSchema = z.enum(guestStatuses);
export type GuestStatus = (typeof guestStatuses)[number];

/** A visitor who may still turn up or still has a live request. */
export const liveGuestStatuses = [
  "awaiting_visitor",
  "pending",
  "active",
] as const satisfies readonly GuestStatus[];

/** States a revoke must never overwrite: a refusal is a decision on record. */
export const terminalGuestStatuses = [
  "rejected",
  "revoked",
] as const satisfies readonly GuestStatus[];

export function isLiveGuestStatus(s: string): boolean {
  return (liveGuestStatuses as readonly string[]).includes(s);
}

/**
 * The one status a collapsed invite row shows: the most hopeful of its
 * visitors. A group with one approved visitor is usable, so it reads active
 * even if a sibling was refused. No guests defers to the invite ("active").
 */
export function collapsedGuestStatus(
  guests: readonly { status: string }[],
): GuestStatus {
  const has = (s: GuestStatus) => guests.some((g) => g.status === s);
  if (guests.length === 0 || has("active")) return "active";
  if (has("pending")) return "pending";
  if (has("awaiting_visitor")) return "awaiting_visitor";
  if (has("rejected")) return "rejected";
  return "revoked";
}

/** « k•••@glovo.com » — enough to recognise a guest, not to harvest one.
 *  THE one email mask of the feature: slice 02's desk, scan and audit labels
 *  consume it (through `guestDisplayName`), never a second copy. Anything
 *  without a non-empty local part comes back as « ••• », never the input.
 *  Splits on the FIRST « @ », like `guestDisplayNameSql`'s `position('@' …)`. */
export function maskGuestEmail(email: string): string {
  const at = email.indexOf("@");
  if (at <= 0) return "•••";
  return `${email.charAt(0)}•••${email.slice(at)}`;
}

/**
 * What to call a visitor on any screen: their name, else their masked email
 * (a self-fill visitor who has not filled in yet), else « Visiteur ».
 *
 * MUST agree with `guestDisplayNameSqlOf` (packages/db/src/invite-guest.ts),
 * which applies the same rule inside queries. Change both or neither.
 */
export function guestDisplayName(g: {
  guestName: string | null;
  guestEmail: string | null;
}): string {
  const name = g.guestName?.trim();
  if (name) return name;
  if (g.guestEmail && g.guestEmail.indexOf("@") >= 1) {
    return maskGuestEmail(g.guestEmail);
  }
  return "Visiteur";
}
